import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Loader2Icon, RefreshCcwIcon } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { BadgeButton } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Text } from '@/components/ui/catalyst/text';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import { useMatter } from '@/hooks/useMatter';
import { useRequestFaxStatus } from '@/hooks/useRequestFaxStatus';
import { hasBlockingMissingFields } from '@/lib/missingFields';
import type {
  EmailSubmissionChannel,
  FaxSubmissionChannel,
  MailingAddressSubmissionChannel,
  PortalSubmissionChannel,
  SubmissionMethod,
  WebsiteSubmissionChannel,
} from '@/services/api/generated/taskActivityService';
import {
  CreateTreatmentRecordRequestChannelSchema,
  FaxTransmissionStatusSchema,
  TreatmentRecordRequestSchema,
  UpdateTreatmentRecordRequestChannelSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { useGetPackageQuery } from '@/services/api/hipaaPackageService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { MedicalProvider } from '@/services/types/treatment-types';
import { getExtendedMetadata } from '@/types/treatment-record-request-extended';
import type { AlternateContact } from '@/types/treatment-request-alternates';

import { ProvidedRecordsDisplay } from '../badges/ProvidedRecordsBadge';
import { InteractiveRequestStatusBadge } from '../badges/RequestStatusBadge';
import { useAlternateContacts } from '../hooks/useAlternateContacts';
import { SelectDestinationModal } from '../modals/SelectDestinationModal';
import { ResendRequestModal } from '../resend/ResendRequestModal';
import { ContactDiscoveryPanel } from '../voice-ai/ContactDiscoveryPanel';
import { RequestResendSection } from '../voice-ai/RequestResendSection';
import { RequestCardOverview } from './RequestCardOverview';

const ChannelTypeLabels: Record<string, string> = {
  portal: 'Portal',
  human: 'Human',
  email: 'Email',
  fax: 'Fax',
  mail: 'Mail',
};

const StatusMessageCard = ({
  title,
  message,
  tone = 'default',
}: {
  title: string;
  message: React.ReactNode;
  tone?: 'default' | 'error';
}) => (
  <div
    className={`rounded-md border p-3 ${
      tone === 'error'
        ? 'border-red-200 bg-red-50'
        : 'border-zinc-200 bg-zinc-50'
    }`}
  >
    <Text
      className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
        tone === 'error' ? 'text-red-700' : 'text-zinc-500'
      }`}
    >
      {title}
    </Text>
    <div
      className={`text-sm ${tone === 'error' ? 'text-red-700' : 'text-zinc-600'}`}
    >
      {message}
    </div>
  </div>
);

const isFetchBaseQueryError = (
  error: unknown,
): error is FetchBaseQueryError => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return 'status' in error;
};

const isNoSubmissionStatusError = (error: unknown): boolean => {
  if (!isFetchBaseQueryError(error)) {
    return false;
  }

  return (
    typeof error.status === 'number' &&
    (error.status === 400 || error.status === 404)
  );
};

const formatMailingAddressHandle = (
  handle?: MailingAddressSubmissionChannel['handle'],
) => {
  if (!handle) return '';
  const locality = [handle.city, handle.state].filter(Boolean).join(', ');
  const cityLine = [locality, handle.zip_code].filter(Boolean).join(' ');
  return [
    handle.recipient,
    handle.address_line_1,
    handle.address_line_2,
    cityLine,
    handle.country,
  ]
    .filter((line) => Boolean(line && line.trim()))
    .join('\n');
};

const buildInstructions = (
  method: SubmissionMethod,
  prepend?: string | null,
) => {
  return (
    [prepend, method.reason, method.submission_instructions]
      .filter((value) => Boolean(value && value.trim()))
      .join('\n\n')
      .trim() || undefined
  );
};

const buildChannelPayloadFromSubmission = (
  method: SubmissionMethod,
): CreateTreatmentRecordRequestChannelSchema | null => {
  const submission = method.submission_method;
  if (!submission) return null;
  const submissionHandleType =
    'handle' in submission &&
    submission.handle &&
    typeof submission.handle === 'object'
      ? (
          submission.handle as { handle_type?: string | null }
        ).handle_type?.toUpperCase()
      : '';
  const channelType =
    submission.channel_type?.toUpperCase() || submissionHandleType || '';

  switch (channelType) {
    case 'FAX': {
      const faxValue = (submission as FaxSubmissionChannel)?.handle?.value;
      if (!faxValue) return null;
      return {
        type: 'fax',
        fax: faxValue,
        instructions: buildInstructions(method),
      };
    }
    case 'EMAIL': {
      const emailValue = (submission as EmailSubmissionChannel)?.handle?.value;
      if (!emailValue) return null;
      return {
        type: 'email',
        email: emailValue,
        instructions: buildInstructions(method),
      };
    }
    case 'MAILING_ADDRESS': {
      const address = formatMailingAddressHandle(
        (submission as MailingAddressSubmissionChannel)?.handle,
      );
      if (!address) return null;
      return {
        type: 'mail',
        address,
        instructions: buildInstructions(method),
      };
    }
    case 'PORTAL': {
      const portalId = (submission as PortalSubmissionChannel)?.portal_id;
      if (!portalId) return null;
      return {
        type: 'portal',
        portal_provider_id: portalId,
        instructions: buildInstructions(method),
      };
    }
    case 'WEBSITE': {
      const website =
        (submission as WebsiteSubmissionChannel)?.website ||
        (submission as WebsiteSubmissionChannel)?.handle ||
        '';
      const instructions = buildInstructions(
        method,
        website ? `Submit via ${website}` : null,
      );
      return {
        type: 'human',
        instructions,
      };
    }
    case 'PHONE': {
      const phoneValue = (submission as FaxSubmissionChannel)?.handle?.value;
      if (!phoneValue) return null;
      return {
        type: 'phone',
        phone_number: phoneValue,
        instructions: buildInstructions(method),
      };
    }
    default:
      return null;
  }
};

const describeChannelPayload = (
  payload: CreateTreatmentRecordRequestChannelSchema,
) => {
  switch (payload.type) {
    case 'fax':
      return `Fax ${payload.fax ?? ''}`.trim();
    case 'email':
      return `Email ${payload.email ?? ''}`.trim();
    case 'mail':
      return 'Mailing address';
    case 'portal':
      return 'Portal submission';
    case 'human':
      return 'Website submission';
    case 'phone':
      return `Phone ${payload.phone_number ?? ''}`.trim();
    default:
      return 'submission method';
  }
};

const buildChannelPayloadFromAlternate = (
  alternate: AlternateContact,
): CreateTreatmentRecordRequestChannelSchema | null => {
  const type = alternate.handle_type.toUpperCase();
  const value = alternate.value?.trim();
  if (!value) return null;

  switch (type) {
    case 'FAX':
      return {
        type: 'fax',
        fax: value,
        instructions: alternate.label ?? undefined,
      };
    case 'PHONE':
      return {
        type: 'phone',
        phone_number: value,
        instructions: alternate.label ?? undefined,
      };
    case 'EMAIL':
      return {
        type: 'email',
        email: value,
        instructions: alternate.label ?? undefined,
      };
    case 'MAILING_ADDRESS':
      return {
        type: 'mail',
        address: value,
        instructions: alternate.label ?? undefined,
      };
    default:
      return null;
  }
};

// Helper functions for formatting
const formatStatusLabel = (status?: string | null) => {
  if (!status) return 'Not started';
  return status
    .toString()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (value?: string | null) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleString();
};

interface DeleteRequestDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
}

const DeleteRequestDialog = ({
  isOpen,
  setIsOpen,
  request,
  provider,
}: DeleteRequestDialogProps) => {
  const { matterId } = useMatter();
  const [deleteRequest, { isLoading }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsDestroyMutation();

  const handleDeleteRequest = () => {
    deleteRequest({
      matterId,
      providerId: provider.id,
      requestId: request.id,
    });
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogBody>
        <DialogTitle>Delete Request</DialogTitle>
        <DialogDescription className="flex flex-col gap-2">
          <div>Are you sure you want to delete this request?</div>
          <div className="text-sm font-bold">This action cannot be undone.</div>
        </DialogDescription>
      </DialogBody>
      <DialogActions>
        <Button outline onClick={() => setIsOpen(false)} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="red" onClick={handleDeleteRequest} disabled={isLoading}>
          {isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Transmission Status Component
interface TransmissionStatusProps {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
}

const TransmissionStatus: React.FC<TransmissionStatusProps> = ({
  request,
  provider,
}) => {
  const { matterId } = useMatter();

  // Use the retrieve endpoint to fetch complete fax transmission data
  // (list endpoint doesn't populate fax_transmission_status field)
  const { faxStatus: fetchedFaxStatus } = useRequestFaxStatus({
    matterId,
    providerId: provider.id,
    requestId: request.id,
    enabled: request.channel?.type === 'fax',
  });

  // Fetch email status using the HIPAA submission status endpoint
  const {
    data: emailStatus,
    error: emailStatusError,
    isFetching: isEmailStatusLoading,
  } = treatmentRecordRequestService.useGetHipaaSubmissionStatusQuery(
    {
      matterId,
      providerId: provider.id,
      requestId: request.id,
    },
    {
      skip: request.channel?.type !== 'email',
    },
  );

  // Only show for fax or email channels
  if (
    !request.channel ||
    (request.channel.type !== 'fax' && request.channel.type !== 'email')
  ) {
    return null;
  }

  // Fax status display
  if (request.channel.type === 'fax') {
    const faxStatus: FaxTransmissionStatusSchema | null | undefined =
      fetchedFaxStatus;

    // Hide the card until at least one transmission attempt exists
    if (!faxStatus?.status) {
      return null;
    }

    return (
      <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3">
        <Text className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
          Fax Transmission Status
        </Text>
        <div className="text-sm text-zinc-600 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-zinc-700">
              {formatStatusLabel(faxStatus?.status)}
            </span>
            {formatDateTime(
              faxStatus?.delivered_at ||
                faxStatus?.sent_at ||
                faxStatus?.failed_at,
            ) && (
              <>
                <span className="text-zinc-400">•</span>
                <span className="text-xs">
                  {formatDateTime(
                    faxStatus?.delivered_at ||
                      faxStatus?.sent_at ||
                      faxStatus?.failed_at,
                  )}
                </span>
              </>
            )}
          </div>
          {faxStatus?.error_message && (
            <div className="text-sm text-red-600">
              {faxStatus.error_message}
            </div>
          )}
          {faxStatus?.transmission_id && (
            <div className="text-xs text-zinc-500">
              Transmission ID: {faxStatus.transmission_id}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Email status display
  if (request.channel.type === 'email') {
    if (isEmailStatusLoading) {
      return (
        <StatusMessageCard
          title="Email Transmission Status"
          message={
            <div className="flex items-center gap-2 text-zinc-600">
              <Loader2Icon className="h-4 w-4 animate-spin text-zinc-400" />
              <span>Checking submission status…</span>
            </div>
          }
        />
      );
    }

    const isKnownNoSubmissionError =
      isNoSubmissionStatusError(emailStatusError);

    if (isKnownNoSubmissionError || (!emailStatus && !emailStatusError)) {
      return (
        <StatusMessageCard
          title="Email Transmission Status"
          message="No submission yet. Send the HIPAA package via email to start tracking delivery."
        />
      );
    }

    if (emailStatusError) {
      return (
        <StatusMessageCard
          title="Email Transmission Status"
          tone="error"
          message="Unable to load email submission status. Please try again later."
        />
      );
    }

    if (!emailStatus) {
      return null;
    }

    // Determine email status from available data
    const getEmailStatus = () => {
      // Use conversation_status if available, otherwise infer from sent_at
      if (emailStatus.conversation_status) {
        return emailStatus.conversation_status;
      }
      if (emailStatus.sent_at) {
        return 'sent';
      }
      return emailStatus.status || 'pending';
    };

    return (
      <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3">
        <Text className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
          Email Transmission Status
        </Text>
        <div className="text-sm text-zinc-600 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-zinc-700">
              {formatStatusLabel(getEmailStatus())}
            </span>
            {formatDateTime(emailStatus.sent_at) && (
              <>
                <span className="text-zinc-400">•</span>
                <span className="text-xs">
                  {formatDateTime(emailStatus.sent_at)}
                </span>
              </>
            )}
          </div>
          {emailStatus.subject && (
            <div className="text-xs text-zinc-500">
              Subject: {emailStatus.subject}
            </div>
          )}
          {emailStatus.recipient_email && (
            <div className="text-xs text-zinc-500">
              To: {emailStatus.recipient_email}
            </div>
          )}
          {emailStatus.front_conversation_url && (
            <a
              href={emailStatus.front_conversation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
            >
              View in Front →
            </a>
          )}
        </div>
      </div>
    );
  }

  return null;
};

interface RequestCardWithTabsProps {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
}

export const RequestCardWithTabs: React.FC<RequestCardWithTabsProps> = ({
  request,
  provider,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { matterId } = useMatter();
  const [isDeleteRequestDialogOpen, setIsDeleteRequestDialogOpen] =
    useState(false);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);
  const extendedMetadata = useMemo(
    () => getExtendedMetadata(request.metadata),
    [request.metadata],
  );
  const phoneNumberRef = useRef<string | null>(
    request.channel?.phone_number ?? null,
  );
  const [applyChannelUpdate, { isLoading: isApplyingExistingChannel }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();
  const [applyChannelCreate, { isLoading: isApplyingNewChannel }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();
  const isChannelMutationInFlight =
    isApplyingExistingChannel || isApplyingNewChannel;

  useEffect(() => {
    const previous = phoneNumberRef.current;
    const current = request.channel?.phone_number ?? null;
    if (!previous && current) {
      toast.success('Auto-selected Records line from provider handles');
    }
    phoneNumberRef.current = current;
  }, [request.channel?.phone_number]);

  // Check if request has been submitted (disable channel editing)
  const submittedStatuses = [
    'request_submitted',
    'request_received',
    'awaiting_invoice',
    'pending_payment',
    'records_pending',
    'records_delivered',
    'records_ingested',
    'rejected',
    'cancelled',
  ];
  const hasSubmittedRequest =
    !!request.status && submittedStatuses.includes(request.status);
  const canEditChannel = !hasSubmittedRequest;

  // Fetch package data to determine CTA state
  const { data: packageData } = useGetPackageQuery(
    {
      matterId,
      providerId: provider.id,
      requestId: request.id,
    },
    {
      skip: !request.id,
    },
  );

  const handleOpenRequestModal = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('request_id', request.id);
    newSearchParams.set('provider_id', provider.id);
    navigate(
      {
        search: newSearchParams.toString(),
      },
      { replace: false },
    );
  };

  const hasChannel = Boolean(request.channel);

  const metadata = request.metadata || {};
  const supersededByRequestId =
    (metadata as Record<string, unknown>)?.superseded_by_request_id ?? null;
  const isSuperseded =
    typeof supersededByRequestId === 'string' && supersededByRequestId !== '';

  const {
    alternates,
    collection: alternatesCollection,
    isLoading: isAlternatesLoading,
  } = useAlternateContacts(matterId, request.task_id, request.metadata);
  const hasFollowUpAlternates = Boolean(
    alternatesCollection?.alternates?.length,
  );

  const resendDisabled = isSuperseded || !hasChannel;

  const getChannelLabel = () => {
    if (!request.channel) return 'Choose Method';
    const baseLabel =
      ChannelTypeLabels[request.channel.type] || request.channel.type;
    return baseLabel;
  };

  // Determine button state based on package data
  const getPackageButtonState = () => {
    if (!packageData) {
      return {
        text: 'View Package',
        icon: EyeIcon,
        color: 'zinc' as const,
      };
    }

    const hasMissingFields = packageData.components?.some((c) =>
      hasBlockingMissingFields(c.missing_fields),
    );

    const isReady = packageData.status === 'ready';
    const isPublished = packageData.status === 'published';

    // Priority 1: Missing info (highest priority)
    // Only consider actual missing fields, not just the status string
    if (hasMissingFields) {
      return {
        text: 'Fix Missing Info',
        icon: ExclamationTriangleIcon,
        color: 'amber' as const,
      };
    }

    // Priority 2: Ready for approval
    if (isReady && !isPublished) {
      return {
        text: 'Approve Package',
        icon: CheckCircleIcon,
        color: 'indigo' as const,
      };
    }

    // Default: Just view
    return {
      text: 'View Package',
      icon: EyeIcon,
      color: 'zinc' as const,
    };
  };

  const buttonState = getPackageButtonState();
  const ButtonIcon = buttonState.icon;

  const callTargetLabel =
    request.channel?.instructions?.split('\n')[0]?.trim() ||
    provider.name ||
    'Medical Records';
  const displayCallTargetLabel = callTargetLabel
    ? callTargetLabel.replace(/\s*\([^)]*\)\s*$/, '').trim() || callTargetLabel
    : callTargetLabel;

  const applyChannelPayload = useCallback(
    async (
      payload: CreateTreatmentRecordRequestChannelSchema,
      successMessage: string,
      actionKey: string,
    ) => {
      if (pendingActionKey || isChannelMutationInFlight) return;
      setPendingActionKey(actionKey);
      try {
        if (request.channel) {
          await applyChannelUpdate({
            matterId,
            providerId: provider.id,
            requestId: request.id,
            channelId: request.channel.id,
            updateTreatmentRecordRequestChannelSchema:
              payload as UpdateTreatmentRecordRequestChannelSchema,
          }).unwrap();
        } else {
          await applyChannelCreate({
            matterId,
            providerId: provider.id,
            requestId: request.id,
            createTreatmentRecordRequestChannelSchema: payload,
          }).unwrap();
        }
        toast.success(successMessage);
      } catch {
        toast.error('Failed to update request channel');
      } finally {
        setPendingActionKey(null);
      }
    },
    [
      applyChannelCreate,
      applyChannelUpdate,
      isChannelMutationInFlight,
      matterId,
      pendingActionKey,
      provider.id,
      request.channel,
      request.id,
    ],
  );

  const handleSubmissionMethodSelect = useCallback(
    async (method: SubmissionMethod, methodKey: string) => {
      const payload = buildChannelPayloadFromSubmission(method);
      if (!payload) {
        toast.warning('Unable to apply this submission method automatically.');
        return;
      }
      await applyChannelPayload(
        payload,
        `Updated request channel to ${describeChannelPayload(payload)}`,
        methodKey,
      );
    },
    [applyChannelPayload],
  );

  const handleAlternateApply = useCallback(
    async (alternate: AlternateContact, alternateKey: string) => {
      const payload = buildChannelPayloadFromAlternate(alternate);
      if (!payload) {
        toast.warning('Unable to apply this contact automatically yet.');
        return;
      }
      await applyChannelPayload(
        payload,
        `Updated request channel to ${describeChannelPayload(payload)}`,
        alternateKey,
      );
    },
    [applyChannelPayload],
  );

  // Build button props conditionally (outline and color are mutually exclusive)
  const buttonProps =
    buttonState.color === 'zinc'
      ? { outline: true as const }
      : { color: buttonState.color };

  return (
    <div className="flex flex-col gap-4 pl-4 border-l-4 border-gray-400">
      {/* Header - uniform height and spacing */}
      <div className="flex items-center gap-2 min-h-[28px]">
        {/* Title with provided records */}
        <ProvidedRecordsDisplay
          records={request.provided_records || []}
          className="flex-shrink-0"
        />

        {/* Channel badge */}
        {canEditChannel ? (
          <BadgeButton
            color="zinc"
            className="text-xs h-6"
            onClick={() => setIsChannelModalOpen(true)}
          >
            {getChannelLabel()}
          </BadgeButton>
        ) : (
          <Tooltip content="Request already submitted. Transmission channel is locked.">
            <span className="inline-flex">
              <BadgeButton
                color="zinc"
                className="text-xs h-6 opacity-60 cursor-not-allowed"
                onClick={(e: React.MouseEvent) => e.preventDefault()}
              >
                {getChannelLabel()}
              </BadgeButton>
            </span>
          </Tooltip>
        )}

        {/* Spacer to push actions to the right */}
        <div className="flex-1" />

        {/* Status and action buttons - uniform height */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <InteractiveRequestStatusBadge
            request={request}
            providerId={provider.id}
          />
          <div className="flex items-center gap-2">
            <Button
              {...buttonProps}
              onClick={handleOpenRequestModal}
              className="flex items-center gap-1.5 h-8"
            >
              <ButtonIcon className="h-4 w-4 flex-shrink-0" />
              {buttonState.text}
            </Button>
            {!isSuperseded && (
              <Button
                outline
                className="flex h-8 items-center justify-center gap-1.5 px-3 text-sm"
                onClick={() => setIsResendModalOpen(true)}
                disabled={resendDisabled}
              >
                <RefreshCcwIcon className="h-4 w-4" />
                Resend
              </Button>
            )}
          </div>
          <Tooltip content="Remove Request">
            <Button
              plain
              onClick={() => setIsDeleteRequestDialogOpen(true)}
              className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>

      {!hasFollowUpAlternates && (
        <ContactDiscoveryPanel
          metadata={extendedMetadata}
          channelPhoneNumber={request.channel?.phone_number}
          targetLabel={displayCallTargetLabel}
          channel={request.channel}
          onSubmissionMethodSelect={
            canEditChannel ? handleSubmissionMethodSelect : undefined
          }
          onAlternateSelect={canEditChannel ? handleAlternateApply : undefined}
          pendingActionKey={pendingActionKey}
        />
      )}

      {/* Transmission Status - for fax and email */}
      <TransmissionStatus request={request} provider={provider} />

      {/* Resend Options & Alternate Contacts */}
      <RequestResendSection
        request={request}
        matterId={matterId}
        providerId={provider.id}
        providerServiceId={provider.provider_service_ref_id}
        alternates={alternates}
        alternatesCollection={alternatesCollection}
        alternatesLoading={isAlternatesLoading}
      />

      {/* Pizza tracker always visible */}
      <RequestCardOverview
        request={request}
        provider={provider}
        matterId={matterId}
      />

      {/* Modals */}
      <DeleteRequestDialog
        isOpen={isDeleteRequestDialogOpen}
        setIsOpen={setIsDeleteRequestDialogOpen}
        request={request}
        provider={provider}
      />

      <SelectDestinationModal
        isOpen={isChannelModalOpen}
        onClose={() => setIsChannelModalOpen(false)}
        request={request}
        providerId={provider.id}
        matterId={matterId}
        provider={provider}
      />

      <ResendRequestModal
        open={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        matterId={matterId}
        providerId={provider.id}
        requestId={request.id}
        providerServiceId={provider.provider_service_ref_id}
        recordType={request.provided_records?.[0]}
        currentScope={request.provided_records?.join(', ')}
        currentChannelType={request.channel?.type}
        currentChannelValue={
          request.channel?.fax ??
          request.channel?.email ??
          request.channel?.phone_number ??
          request.channel?.address ??
          request.channel?.instructions ??
          undefined
        }
      />
    </div>
  );
};
